import React, {useEffect, useRef} from "react";
import s from "./scheduleEditor.module.css";
import {Checkbox} from "../input/checkbox";
import {copyDay, Day, DAY_SHORT, DAYS, DayMode, DaySchedule, ScheduleForm, ScheduleRow, validateSchedule} from "../../hooks/menu/schedule";

// [nominative, accusative]
const DAY_NAME: Record<Day, [string, string]> = {
    monday: ["Понедельник", "понедельник"],
    tuesday: ["Вторник", "вторник"],
    wednesday: ["Среда", "среду"],
    thursday: ["Четверг", "четверг"],
    friday: ["Пятница", "пятницу"],
    saturday: ["Суббота", "субботу"],
    sunday: ["Воскресенье", "воскресенье"],
};
const MODES: [DayMode, string][] = [["allDay", "Весь день"], ["hours", "По часам"], ["hidden", "Не показывать"]];
const EMPTY_ROW: ScheduleRow = {start: "", end: "", toEndOfDay: false};

interface Props {
    value: ScheduleForm
    onChange: (value: ScheduleForm) => void
    // Errors show from the first blocked save on; each blocked save moves focus to the first error.
    failedSaves: number
}

export function ScheduleEditor({value, onChange, failedSaves}: Props) {
    const root = useRef<HTMLDivElement>(null);
    const {errors, warning} = validateSchedule(value);
    const errorsOf = (day: Day, row: number | null) =>
        failedSaves > 0 ? errors.filter(e => e.day === day && e.row === row).map(e => e.message) : [];

    useEffect(() => {
        if (failedSaves === 0) return;
        // Invalid inputs carry their error via aria-describedby; a day without rows focuses its error text.
        const target = root.current?.querySelector<HTMLElement>('input[aria-invalid="true"], [data-day-error]');
        target?.focus({preventScroll: true});
        target?.scrollIntoView({block: "center", behavior: "smooth"});
    }, [failedSaves]);

    const copy = (e: React.MouseEvent<HTMLButtonElement>, from: Day, to: "weekdays" | "all") => {
        e.currentTarget.closest("details")?.removeAttribute("open");
        onChange(copyDay(value, from, to));
    };

    const setDay = (day: Day, patch: Partial<DaySchedule>) =>
        onChange({...value, days: {...value.days, [day]: {...value.days[day], ...patch}}});
    const setRow = (day: Day, index: number, patch: Partial<ScheduleRow>) =>
        setDay(day, {rows: value.days[day].rows.map((row, i) => i === index ? {...row, ...patch} : row)});

    return (
        <div className="control" ref={root}>
            <Checkbox checked={value.enabled} label="Показывать по расписанию" style={{marginBottom: 0}}
                      onChange={enabled => onChange({...value, enabled})}/>
            {!value.enabled && <div className={s.hint}>Раздел показывается всегда</div>}

            {value.enabled && <>
                {DAYS.map(day => {
                    const {mode, rows} = value.days[day];
                    const [nominative, accusative] = DAY_NAME[day];
                    return (
                        <div key={day} className={s.day}>
                            <div className={s.dayHead}>
                                <span className={s.dayName} aria-hidden="true">{DAY_SHORT[day]}</span>
                                <div role="radiogroup" aria-label={`${nominative}: режим показа`} className={s.modes}>
                                    {MODES.map(([m, text]) => (
                                        <label key={m}>
                                            <input type="radio" name={`schedule-${day}`} checked={mode === m}
                                                   onChange={() => setDay(day, m === "hours" && rows.length === 0 ? {mode: m, rows: [EMPTY_ROW]} : {mode: m})}/>
                                            <span>{text}</span>
                                        </label>
                                    ))}
                                </div>
                                <details className={s.copy}>
                                    <summary>копировать ▾</summary>
                                    <button type="button" aria-label={`Копировать ${accusative} на будни`}
                                            onClick={e => copy(e, day, "weekdays")}>на будни</button>
                                    <button type="button" aria-label={`Копировать ${accusative} на все дни`}
                                            onClick={e => copy(e, day, "all")}>на все дни</button>
                                </details>
                            </div>
                            {errorsOf(day, null).map(message =>
                                <div key={message} className={s.error} data-day-error tabIndex={-1}>{message}</div>)}

                            {mode === "hours" && <div className={s.rows}>
                                {rows.map((row, i) => {
                                    const rowErrors = errorsOf(day, i);
                                    const errorId = rowErrors.length > 0 ? `schedule-${day}-${i}-error` : undefined;
                                    return (
                                        <div key={i}>
                                            <div className={s.row}>
                                                <label>С <input type="time" step={60} className="control__input" value={row.start}
                                                                 aria-invalid={!!errorId} aria-describedby={errorId}
                                                                 onChange={e => setRow(day, i, {start: e.target.value})}/></label>
                                                <label>До <input type="time" step={60} className="control__input" value={row.end}
                                                                  disabled={row.toEndOfDay} aria-invalid={!!errorId} aria-describedby={errorId}
                                                                  onChange={e => setRow(day, i, {end: e.target.value})}/></label>
                                                <label className={s.endOfDay}>
                                                    <input type="checkbox" checked={row.toEndOfDay}
                                                           onChange={e => setRow(day, i, {toEndOfDay: e.target.checked})}/>
                                                    до конца дня
                                                </label>
                                                <button type="button" className={s.remove} aria-label="Удалить интервал"
                                                        onClick={() => setDay(day, {rows: rows.filter((_, j) => j !== i)})}>×</button>
                                            </div>
                                            {errorId && <div id={errorId} className={s.error}>{rowErrors.join(". ")}</div>}
                                        </div>
                                    );
                                })}
                                <button type="button" className={s.add} onClick={() => setDay(day, {rows: [...rows, EMPTY_ROW]})}>
                                    + Добавить интервал
                                </button>
                            </div>}
                        </div>
                    );
                })}
                {warning && <div className={s.warning}>{warning}</div>}
                <div className={s.note}>Изменения расписания начнут действовать после публикации меню</div>
            </>}
        </div>
    );
}
